import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import GoldSiverHeader from '../HomePage/GoldSiverHeader';
import axios from '../../api/axios';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Modal, Button } from 'react-bootstrap'; // Using react-bootstrap Modal
import '../../assets/css/pokemonMarket.css'; // Import standard styles

const Guild = () => {
    const { t, i18n } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [inGuild, setInGuild] = useState(false);
    const [guildData, setGuildData] = useState(null);
    const [guildsList, setGuildsList] = useState([]);
    const [page, setPage] = useState(1);
    
    // Create Guild Form
    const [createData, setCreateData] = useState({ name: '', tag: '', description: '' });
    const [creating, setCreating] = useState(false);

    // Settings Form
    const [settingsData, setSettingsData] = useState({ description: '', is_private: false });

    // My Guild Tabs
    const [activeTab, setActiveTab] = useState('home');

    // Management Modal
    const [showManageModal, setShowManageModal] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [actionType, setActionType] = useState(null); // 'kick', 'promote', 'demote'

    const userRedux = useSelector(state => state.auth.user_data);
    const isRTL = i18n.language === 'he';

    useEffect(() => {
        checkMyGuild();
    }, []);

    const checkMyGuild = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/api/api/guild.php?action=my_guild');
            if (response.data.success) {
                if (response.data.in_guild) {
                    setInGuild(true);
                    setGuildData(response.data);
                    // Init settings form
                    setSettingsData({
                        description: response.data.guild.description || '',
                        is_private: response.data.guild.is_private === '1'
                    });
                } else {
                    setInGuild(false);
                    fetchGuildsList();
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchGuildsList = async () => {
        try {
            const response = await axios.get(`/api/api/guild.php?action=list&page=${page}`);
            if (response.data.success) {
                setGuildsList(response.data.guilds);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const handleCreateGuild = async (e) => {
        e.preventDefault();
        setCreating(true);
        try {
            const response = await axios.post('/api/api/guild.php', {
                action: 'create',
                ...createData
            });
            if (response.data.success) {
                toast.success(t('Guild created successfully!'));
                checkMyGuild();
            }
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to create guild');
        } finally {
            setCreating(false);
        }
    };

    const handleJoin = async (guildId) => {
        try {
            const response = await axios.post('/api/api/guild.php', {
                action: 'join',
                guild_id: guildId
            });
            if (response.data.success) {
                toast.success(t(response.data.message));
                checkMyGuild();
            }
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to join guild');
        }
    };

    const handleDonate = async (amount, currency) => {
        try {
            const response = await axios.post('/api/api/guild.php', {
                action: 'donate',
                amount, 
                currency
            });
            if (response.data.success) {
                const coins = response.data.coins_earned || 0;
                toast.success(t('Donation successful!') + (coins > 0 ? ` +${coins} ${t('Guild Coins')}` : ''));
                checkMyGuild(); // Refresh
            }
        } catch (error) {
            toast.error(error.response?.data?.error || 'Donation failed');
        }
    };

    const handleUpgrade = async (type) => {
        try {
            const response = await axios.post('/api/api/guild.php', {
                action: 'upgrade',
                type
            });
            if (response.data.success) {
                toast.success(t('Upgrade successful!'));
                checkMyGuild(); // Refresh
            }
        } catch (error) {
            toast.error(error.response?.data?.error || 'Upgrade failed');
        }
    };

    const handleClaimMission = async (mission_id) => {
        try {
            const response = await axios.post('/api/api/guild.php', {
                action: 'claim_mission',
                mission_id
            });
            if (response.data.success) {
                const rewards = response.data.rewards;
                let msg = t('Mission reward claimed!');
                if(rewards.coins) msg += ` +${rewards.coins} ${t('Guild Coins')}`;
                toast.success(msg);
                checkMyGuild(); // Refresh
            }
        } catch (error) {
            toast.error(error.response?.data?.error || 'Claim failed');
        }
    };

    // Management Actions
    const handleUpdateSettings = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post('/api/api/guild.php', {
                action: 'update_settings',
                ...settingsData
            });
            if (response.data.success) {
                toast.success(t('Settings updated.'));
                checkMyGuild();
            }
        } catch (error) {
            toast.error(error.response?.data?.error || 'Update failed');
        }
    };

    const handleManageRequest = async (requestId, action) => {
        try {
            const response = await axios.post('/api/api/guild.php', {
                action: 'manage_request',
                request_id: requestId,
                req_action: action
            });
            if (response.data.success) {
                toast.success(t('Request handled.'));
                checkMyGuild();
            }
        } catch (error) {
            toast.error(error.response?.data?.error || 'Action failed');
        }
    };

    const openManageModal = (action, member) => {
        setSelectedMember(member);
        setActionType(action);
        setShowManageModal(true);
    };

    const confirmMemberAction = async () => {
        if (!selectedMember || !actionType) return;
        
        try {
            const response = await axios.post('/api/api/guild.php', {
                action: actionType, // kick, promote, demote
                target_id: selectedMember.user_id
            });
            if (response.data.success) {
                toast.success(t('Action successful.'));
                checkMyGuild();
                setShowManageModal(false);
            }
        } catch (error) {
            toast.error(error.response?.data?.error || 'Action failed');
        }
    };
    
    const handleLeaveGuild = async () => {
        if(!window.confirm(t('Are you sure you want to leave? You will lose all your Guild Coins!'))) return;
        try {
            const response = await axios.post('/api/api/guild.php', {
                action: 'leave'
            });
            if (response.data.success) {
                toast.success(t('You left the guild.'));
                setInGuild(false);
                setGuildData(null);
                fetchGuildsList();
            }
        } catch (error) {
            toast.error(error.response?.data?.error || 'Leave failed');
        }
    };
    
    const handleBuyItem = async (itemId) => {
        if(!window.confirm(t('Buy this item?'))) return;
        try {
            const response = await axios.post('/api/api/guild.php', {
                action: 'buy_item',
                item_id: itemId
            });
            if (response.data.success) {
                toast.success(t('Item purchased!'));
                checkMyGuild();
            }
        } catch (error) {
            toast.error(error.response?.data?.error || 'Purchase failed');
        }
    };

    if (loading) return <div style={{textAlign: 'center', color: 'white', marginTop: '50px'}}>{t('Loading...')}</div>;

    // View: Not in Guild (List + Create)
    if (!inGuild) {
        return (
            <div className="ar_mainBody" dir={isRTL ? 'rtl' : 'ltr'}>
                <GoldSiverHeader previous="/home" title={t('Guild')}>
                    <section className="ar_market_filter_section">
                         <div className="container">
                            <div className="row">
                                <div className="col-md-12">
                                     <div className="ar_market_filter_title">
                                        <div className="ar_market_cover_image_wrapper">
                                            <img src="/images/cover/1.png" alt="Guild Cover" className="ar_market_cover_image" />
                                        </div>
                                        <div className="ar_market_cover_overlay"></div>
                                        <div className="ar_market_cover_content">
                                            <h1>{t('Guilds System')}</h1>
                                            <p>{t('Join a guild or create your own to unlock exclusive features!')}</p>
                                        </div>
                                     </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="ar_market_table_section">
                        <div className="container">
                            <div className="row">
                                <div className="col-md-8">
                                    <div className="ar_market_table_area">
                                        <h4>{t('Guild List')}</h4>
                                        <div className="ar_market_table_wrapper">
                                            <table className="ar_market_table">
                                                <thead>
                                                    <tr>
                                                        <th>{t('Tag')}</th>
                                                        <th>{t('Name')}</th>
                                                        <th>{t('Level')}</th>
                                                        <th>{t('Members')}</th>
                                                        <th>{t('Action')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {guildsList.length === 0 ? (
                                                        <tr><td colSpan="5" className="ar_market_no_data">{t('No guilds found')}</td></tr>
                                                    ) : (
                                                        guildsList.map(g => (
                                                            <tr key={g.id}>
                                                                <td>[{g.tag}]</td>
                                                                <td>{g.name} {g.is_private === '1' && '🔒'}</td>
                                                                <td>{g.level}</td>
                                                                <td>{g.real_count}</td>
                                                                <td>
                                                                    <button className="ar_market_buy_btn" onClick={() => handleJoin(g.id)}>
                                                                         {g.is_private === '1' ? t('Request') : t('Join')}
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-md-4">
                                    <div className="ar_market_filter_area">
                                        <h4>{t('Create Guild')}</h4>
                                        <form onSubmit={handleCreateGuild} className="ar_market_filter_form">
                                            <div className="ar_market_filter_field">
                                                <label>{t('Guild Name')}</label>
                                                <input className="form-control" value={createData.name} onChange={e => setCreateData({...createData, name: e.target.value})} maxLength={20} required />
                                            </div>
                                            <div className="ar_market_filter_field">
                                                <label>{t('Guild Tag')}</label>
                                                <input className="form-control" value={createData.tag} onChange={e => setCreateData({...createData, tag: e.target.value})} maxLength={5} required />
                                            </div>
                                            <div className="ar_market_filter_field">
                                                <label>{t('Description')}</label>
                                                <textarea className="form-control" value={createData.description} onChange={e => setCreateData({...createData, description: e.target.value})} maxLength={200} />
                                            </div>
                                            <div className="ar_market_filter_actions">
                                                <button type="submit" className="ar_market_search_btn" disabled={creating}>
                                                    {creating ? t('Creating...') : t('Create (50k Silver)')}
                                                </button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </GoldSiverHeader>
            </div>
        );
    }

    // View: In Guild (Dashboard)
    const { guild, members, missions, user_role, requests, shop_items, my_guild_coins } = guildData;
    const isOfficer = user_role >= 3;
    const isLeader = user_role === 4;
    
    return (
        <div className="ar_mainBody" dir={isRTL ? 'rtl' : 'ltr'}>
            <GoldSiverHeader previous="/home" title={t('Guild')}>
                {/* Header Section */}
                <section className="ar_market_filter_section">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="ar_market_filter_title">
                                    <div className="ar_market_cover_image_wrapper">
                                        <img src="/images/cover/1.png" alt="Guild Cover" className="ar_market_cover_image" />
                                    </div>
                                    <div className="ar_market_cover_overlay"></div>
                                    <div className="ar_market_cover_content">
                                        <h1>[{guild.tag}] {guild.name}</h1>
                                        <p>{guild.description}</p>
                                        <div style={{marginTop: '15px', display: 'flex', gap: '20px', justifyContent: 'center'}}>
                                            <span className="ar_market_feature_item" style={{color: 'white'}}>Lv. {guild.level}</span>
                                            <span className="ar_market_feature_item" style={{color: 'white'}}>{guild.members_count} {t('Members')}</span>
                                            <span className="ar_market_feature_item" style={{color: 'white'}}>{parseInt(guild.xp).toLocaleString()} XP</span>
                                        </div>
                                        <button className="btn btn-danger btn-sm mt-3" onClick={handleLeaveGuild}>{t('Leave Guild')}</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Tabs Section */}
                <section className="ar_market_tabs_section">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="ar_market_tabs_area">
                                    <ul className="ar_market_tabs_list">
                                        <li className={activeTab === 'home' ? 'active' : ''}>
                                            <a href="#" onClick={(e) => { e.preventDefault(); setActiveTab('home'); }}>{t('Home')}</a>
                                        </li>
                                        <li className={activeTab === 'members' ? 'active' : ''}>
                                            <a href="#" onClick={(e) => { e.preventDefault(); setActiveTab('members'); }}>{t('Members')}</a>
                                        </li>
                                        <li className={activeTab === 'bank' ? 'active' : ''}>
                                            <a href="#" onClick={(e) => { e.preventDefault(); setActiveTab('bank'); }}>{t('Bank')}</a>
                                        </li>
                                        <li className={activeTab === 'upgrades' ? 'active' : ''}>
                                            <a href="#" onClick={(e) => { e.preventDefault(); setActiveTab('upgrades'); }}>{t('Upgrades')}</a>
                                        </li>
                                        <li className={activeTab === 'missions' ? 'active' : ''}>
                                            <a href="#" onClick={(e) => { e.preventDefault(); setActiveTab('missions'); }}>{t('Missions')}</a>
                                        </li>
                                        <li className={activeTab === 'shop' ? 'active' : ''}>
                                            <a href="#" onClick={(e) => { e.preventDefault(); setActiveTab('shop'); }}>{t('Shop')}</a>
                                        </li>
                                        {isOfficer && (
                                            <li className={activeTab === 'manage' ? 'active' : ''}>
                                                <a href="#" onClick={(e) => { e.preventDefault(); setActiveTab('manage'); }}>{t('Manage')}</a>
                                            </li>
                                        )}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Content Section */}
                <section className="ar_market_table_section">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="ar_market_table_area">
                                    
                                    {activeTab === 'home' && (
                                        <div className="text-center">
                                            <h4>{t('Guild Announcement')}</h4>
                                            <div className="p-4 mb-4" style={{background: 'rgba(0,0,0,0.05)', borderRadius: '10px'}}>
                                                <p style={{fontSize: '18px', color: '#0a2865'}}>{guild.announcement || t('No announcements yet.')}</p>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-4 offset-md-4">
                                                    <div className="ar_backpack_capacity_item">
                                                        <strong>{t('My Guild Coins')}:</strong>
                                                        <span>{parseInt(my_guild_coins).toLocaleString()}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {activeTab === 'members' && (
                                        <div className="ar_market_table_wrapper">
                                            <table className="ar_market_table">
                                                <thead>
                                                    <tr>
                                                        <th>{t('Name')}</th>
                                                        <th>{t('Role')}</th>
                                                        <th>{t('Rank')}</th>
                                                        <th>{t('Donated')}</th>
                                                        <th>{t('Status')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {members.map(m => (
                                                        <tr key={m.id}>
                                                            <td>{m.username}</td>
                                                            <td>
                                                                {m.role === 4 ? <span className="badge bg-warning text-dark">{t('Leader')}</span> : 
                                                                 m.role === 3 ? <span className="badge bg-info">{t('Officer')}</span> : 
                                                                 m.role === 2 ? <span className="badge bg-secondary">{t('Veteran')}</span> : t('Member')}
                                                            </td>
                                                            <td>{m.rank}</td>
                                                            <td>{parseInt(m.donated_silver).toLocaleString()} <img src="/images/mock-05.png" width="16" alt="S"/></td>
                                                            <td>
                                                                <span style={{color: m.is_online ? 'green' : 'gray'}}>
                                                                    {m.is_online ? '● Online' : '○ Offline'}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    {activeTab === 'bank' && (
                                        <div className="row">
                                            <div className="col-md-12 text-center mb-4">
                                                <div className="ar_backpack_capacity_info">
                                                    <div className="ar_backpack_capacity_item">
                                                        <strong>{t('Guild Silver')}:</strong>
                                                        <span>{parseInt(guild.silver_bank).toLocaleString()}</span>
                                                    </div>
                                                    <div className="ar_backpack_capacity_item">
                                                        <strong>{t('Guild Gold')}:</strong>
                                                        <span>{parseInt(guild.gold_bank).toLocaleString()}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="ar_market_filter_area">
                                                    <h4>{t('Donate Silver')}</h4>
                                                    <p className="text-center text-muted">{t('1,000 Silver = 1 Guild Coin')}</p>
                                                    <div className="d-flex justify-content-center gap-2">
                                                        <button className="ar_market_buy_btn" onClick={() => handleDonate(1000, 'silver')}>1k</button>
                                                        <button className="ar_market_buy_btn" onClick={() => handleDonate(10000, 'silver')}>10k</button>
                                                        <button className="ar_market_buy_btn" onClick={() => handleDonate(50000, 'silver')}>50k</button>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="ar_market_filter_area">
                                                    <h4>{t('Donate Gold')}</h4>
                                                    <p className="text-center text-muted">{t('1 Gold = 10 Guild Coins')}</p>
                                                    <div className="d-flex justify-content-center gap-2">
                                                        <button className="ar_market_bid_btn" onClick={() => handleDonate(10, 'gold')}>10</button>
                                                        <button className="ar_market_bid_btn" onClick={() => handleDonate(50, 'gold')}>50</button>
                                                        <button className="ar_market_bid_btn" onClick={() => handleDonate(100, 'gold')}>100</button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {activeTab === 'upgrades' && (
                                        <div className="row">
                                            {Object.entries(guild.upgrades || {}).map(([key, level]) => {
                                                const cost = guild.upgrade_costs?.[key] || 0;
                                                return (
                                                    <div key={key} className="col-md-6 mb-4">
                                                        <div className="ar_market_filter_area" style={{padding: '20px'}}>
                                                            <h5 style={{color: '#0a2865', fontWeight: 'bold'}}>{t(key.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()))}</h5>
                                                            <div className="d-flex justify-content-between mb-2">
                                                                <span>{t('Current Level')}:</span>
                                                                <strong>{level}</strong>
                                                            </div>
                                                            <div className="alert alert-info py-2" style={{fontSize: '14px'}}>
                                                                {key === 'training_room' && `+${level * 5}% EXP Bonus`}
                                                                {key === 'silver_bonus' && `+${level * 5}% Silver Bonus`}
                                                                {key === 'members_capacity' && `Max Members: ${10 + level * 5}`}
                                                            </div>
                                                            {level < 10 ? (
                                                                isOfficer ? (
                                                                    <button 
                                                                        className="ar_market_buy_btn w-100" 
                                                                        onClick={() => handleUpgrade(key)}
                                                                        disabled={guild.silver_bank < cost}
                                                                    >
                                                                        {t('Upgrade')} ({parseInt(cost).toLocaleString()} S)
                                                                    </button>
                                                                ) : (
                                                                    <div className="text-center text-muted fst-italic">
                                                                        {t('Officers only')}
                                                                    </div>
                                                                )
                                                            ) : (
                                                                <button className="btn btn-success w-100" disabled>{t('Max Level')}</button>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {activeTab === 'missions' && (
                                        <div className="row">
                                            {missions && missions.length > 0 ? missions.map(m => (
                                                <div key={m.id} className="col-md-6 mb-3">
                                                    <div className="ar_market_filter_area" style={{padding: '20px'}}>
                                                        <h5>{t(m.name_key)}</h5>
                                                        <div className="d-flex justify-content-between mb-1">
                                                            <small>{t('Progress')}</small>
                                                            <small>{m.progress} / {m.target_value}</small>
                                                        </div>
                                                        <div className="progress mb-2" style={{height: '10px'}}>
                                                            <div className="progress-bar bg-warning" role="progressbar" style={{width: `${Math.min(100, (m.progress / m.target_value) * 100)}%`}}></div>
                                                        </div>
                                                        <div className="mb-2" style={{fontSize: '13px'}}>
                                                            <strong>{t('Rewards')}:</strong> {m.reward_xp} XP, {m.reward_silver} S, {m.reward_coins} Coins
                                                        </div>
                                                        {m.is_completed === '1' ? (
                                                            m.is_claimed === '1' ? (
                                                                <button className="btn btn-secondary w-100 btn-sm" disabled>{t('Claimed')}</button>
                                                            ) : (
                                                                <button className="btn btn-success w-100 btn-sm" onClick={() => handleClaimMission(m.id)}>{t('Claim Reward')}</button>
                                                            )
                                                        ) : (
                                                            <button className="btn btn-outline-secondary w-100 btn-sm" disabled>{t('In Progress')}</button>
                                                        )}
                                                    </div>
                                                </div>
                                            )) : (
                                                <div className="text-center p-5 text-muted">{t('No active missions.')}</div>
                                            )}
                                        </div>
                                    )}

                                    {activeTab === 'shop' && (
                                        <div>
                                            <div className="ar_backpack_capacity_info mb-4">
                                                <div className="ar_backpack_capacity_item">
                                                    <strong>{t('My Wallet')}:</strong>
                                                    <span>{parseInt(my_guild_coins).toLocaleString()} 🪙</span>
                                                </div>
                                            </div>
                                            <div className="row">
                                                {shop_items && shop_items.length > 0 ? shop_items.map(item => (
                                                    <div key={item.id} className="col-md-4 mb-4">
                                                        <div className="ar_market_filter_area text-center" style={{padding: '20px'}}>
                                                            <img src={`/images/items/${item.image}`} alt={item.name_key} style={{width: '60px', height: '60px', objectFit: 'contain'}} onError={(e) => e.target.src = '/images/items/potion.png'}/>
                                                            <h5 className="mt-2">{item.name_key}</h5>
                                                            <p className="fw-bold text-warning">{item.cost} 🪙</p>
                                                            <p className="small text-muted">{t('Req. Guild Lv.')} {item.min_guild_level}</p>
                                                            <button 
                                                                className="ar_market_buy_btn w-100"
                                                                onClick={() => handleBuyItem(item.id)}
                                                                disabled={my_guild_coins < item.cost || guild.level < item.min_guild_level}
                                                            >
                                                                {t('Buy')}
                                                            </button>
                                                        </div>
                                                    </div>
                                                )) : (
                                                    <div className="col-12 text-center text-muted">{t('Shop is empty.')}</div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {activeTab === 'manage' && isOfficer && (
                                        <div>
                                            <div className="ar_market_filter_area mb-4">
                                                <h4>{t('Guild Settings')}</h4>
                                                <form onSubmit={handleUpdateSettings} className="ar_market_filter_form">
                                                    <div className="ar_market_filter_field">
                                                        <label>{t('Description')}</label>
                                                        <textarea className="form-control" value={settingsData.description} onChange={e => setSettingsData({...settingsData, description: e.target.value})} />
                                                    </div>
                                                    <div className="form-check form-switch mb-3">
                                                        <input className="form-check-input" type="checkbox" id="privateSwitch" checked={settingsData.is_private} onChange={e => setSettingsData({...settingsData, is_private: e.target.checked})} />
                                                        <label className="form-check-label" htmlFor="privateSwitch">{t('Private (Invite/Request Only)')}</label>
                                                    </div>
                                                    <div className="text-center">
                                                        <button type="submit" className="ar_market_search_btn btn-sm" style={{padding: '10px 30px'}}>{t('Save Settings')}</button>
                                                    </div>
                                                </form>
                                            </div>

                                            {requests && requests.length > 0 && (
                                                <div className="mb-4">
                                                    <h4>{t('Join Requests')}</h4>
                                                    <div className="ar_market_table_wrapper">
                                                        <table className="ar_market_table">
                                                            <tbody>
                                                                {requests.map(req => (
                                                                    <tr key={req.id}>
                                                                        <td>{req.username}</td>
                                                                        <td>Lv. {req.rank}</td>
                                                                        <td>
                                                                            <button className="btn btn-success btn-sm me-2" onClick={() => handleManageRequest(req.id, 'accept')}>{t('Accept')}</button>
                                                                            <button className="btn btn-danger btn-sm" onClick={() => handleManageRequest(req.id, 'reject')}>{t('Reject')}</button>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            )}

                                            <div>
                                                <h4>{t('Member Management')}</h4>
                                                <div className="ar_market_table_wrapper">
                                                    <table className="ar_market_table">
                                                        <thead>
                                                            <tr>
                                                                <th>{t('Name')}</th>
                                                                <th>{t('Role')}</th>
                                                                <th>{t('Actions')}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {members.map(m => (
                                                                <tr key={m.id}>
                                                                    <td>{m.username}</td>
                                                                    <td>{m.role === 4 ? t('Leader') : m.role === 3 ? t('Officer') : m.role === 2 ? t('Veteran') : t('Member')}</td>
                                                                    <td>
                                                                        {parseInt(m.role) < parseInt(user_role) ? (
                                                                            <div className="d-flex gap-1 justify-content-center">
                                                                                <button className="btn btn-danger btn-sm" style={{padding: '2px 8px'}} title={t('Kick')} onClick={() => openManageModal('kick', m)}>❌</button>
                                                                                {isLeader && (
                                                                                    <>
                                                                                        <button className="btn btn-info btn-sm" style={{padding: '2px 8px'}} title={t('Promote')} onClick={() => openManageModal('promote', m)}>▲</button>
                                                                                        <button className="btn btn-warning btn-sm" style={{padding: '2px 8px'}} title={t('Demote')} onClick={() => openManageModal('demote', m)}>▼</button>
                                                                                    </>
                                                                                )}
                                                                            </div>
                                                                        ) : (
                                                                            <span className="text-muted" style={{fontSize: '12px'}}>-</span>
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                
                {/* Management Modal */}
                <Modal show={showManageModal} onHide={() => setShowManageModal(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>{t('Confirm Action')}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>
                            {t('Are you sure you want to')} <strong>{actionType}</strong> {selectedMember?.username}?
                        </p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowManageModal(false)}>
                            {t('Cancel')}
                        </Button>
                        <Button variant="primary" onClick={confirmMemberAction}>
                            {t('Confirm')}
                        </Button>
                    </Modal.Footer>
                </Modal>

            </GoldSiverHeader>
        </div>
    );
};

export default Guild;
